import { IBboxType } from 'annotation-tools/annotation-canvas';
import {
  combineLatestTyped,
  filterNotUndefined,
  getGroupedSetDiff,
  IList,
  IRect,
  ISet,
  mergeTyped,
  roundRectToInt,
  toAbsoluteRect,
  useDataStream,
  useEventAsStream,
  useStreamEffect,
  withLatestValuesFrom
} from 'annotation-tools/utils';
import { Observable } from 'rxjs';
import { filter, map, pairwise, distinctUntilChanged } from 'rxjs/operators';
import {
  createProjectImageAnnotation,
  deleteProjectImageAnnotation,
  updateProjectImageAnnotation
} from '../../../../api/fetch-functions/project-image-annotation';
import { token$ } from '../../../../global-state/auth-state';
import { fetchProjectImageListOf } from '../../../../global-state/database/project-image-list';
import { imageId$, projectId$ } from '../../../../global-state/router-id';
import { IProjectImageInfoType } from '../../../../types/record/image-info';

type BboxListReducerState = {
  lastOp: 'canvas' | 'input' | 'infer';
  data: IList<IBboxType>;
};

export const useBboxList = (
  bboxRelativeListInput$: Observable<IList<IBboxType> | undefined>,
  inferenceResult$: Observable<IList<IBboxType>>,
  setPostResultType: (value: 'error' | 'success') => void,
  imageInfo$: Observable<IProjectImageInfoType>
) => {
  const [
    // dummy comment to control prettier
    bboxRelativeListFromCanvas$,
    setBboxRelativeListFromCanvas
  ] = useEventAsStream<IList<IBboxType>>();

  const bboxRelativeListMerged$ = useDataStream<
    BboxListReducerState | undefined
  >(
    undefined,
    mergeTyped(
      combineLatestTyped(
        bboxRelativeListInput$.pipe(filterNotUndefined()),
        imageId$, // ページ遷移時にinputで初期化
        imageInfo$.pipe(
          map(a => a.modelId),
          distinctUntilChanged()
        ) // modelIdが変わったときにinputで初期化
      ).pipe(map(([a]) => ({ lastOp: 'input' as const, data: a }))),
      bboxRelativeListFromCanvas$.pipe(
        map(a => ({ lastOp: 'canvas' as const, data: a }))
      ),
      inferenceResult$.pipe(map(a => ({ lastOp: 'infer' as const, data: a })))
    )
  );

  const bboxRelativeListListDiff$ = useDataStream<
    | {
        deleted: ISet<IBboxType>;
        edited: ISet<IBboxType>;
        added: ISet<IBboxType>;
      }
    | undefined
  >(
    undefined,
    bboxRelativeListMerged$.pipe(
      pairwise(),
      filter(([_prev, curr]) => curr?.lastOp === 'canvas'),
      map(([prev, curr]) => {
        if (prev === undefined || curr === undefined) return undefined;

        const diff = getGroupedSetDiff(
          prev.data.toSet(),
          curr.data.toSet(),
          e => e.id
        );

        return {
          deleted: diff.get('deleted') ?? ISet<IBboxType>(),
          edited: diff.get('updated') ?? ISet<IBboxType>(),
          added: diff.get('added') ?? ISet<IBboxType>()
        };
      })
    )
  );

  useStreamEffect(
    bboxRelativeListListDiff$.pipe(
      withLatestValuesFrom(token$, projectId$, imageInfo$)
    ),
    ([bboxListDiff, [token, projectId, image]]) => {
      if (token === undefined || bboxListDiff === undefined) return;

      const { added, edited, deleted } = bboxListDiff;

      Promise.all([
        ...added.map(bbox =>
          createProjectImageAnnotation(token)(
            projectId,
            image.id
          )({
            labelId: bbox.label.id as number, // TODO: remove cast
            rect: roundRectToInt(toAbsoluteRect(IRect(image.size))(bbox.rect)),
            uuid: bbox.id as string
          }).then(res => res.type)
        ),
        ...edited.map(bbox =>
          updateProjectImageAnnotation(token)(
            projectId,
            image.id,
            bbox.id as string
          )({
            labelId: bbox.label.id as number,
            rect: roundRectToInt(toAbsoluteRect(IRect(image.size))(bbox.rect))
          }).then(res => res.type)
        ),
        ...deleted.map(bbox =>
          deleteProjectImageAnnotation(token)(
            projectId,
            image.id,
            bbox.id as string
          ).then(res => res.type)
        )
      ]).then(allResultType => {
        if (allResultType.every(e => e === 'success')) {
          setPostResultType('success');
        } else {
          setPostResultType('error');
        }
        fetchProjectImageListOf(projectId);
      });
    }
  );

  const bboxRelativeList$ = useDataStream<IList<IBboxType> | undefined>(
    undefined,
    bboxRelativeListMerged$.pipe(map(a => a?.data))
  );

  return {
    bboxRelativeListFromCanvas$,
    setBboxRelativeListFromCanvas,
    bboxRelativeList$
  };
};
